import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export interface CopilotPagePrompt {
  id?: string;
  title?: string;
  prompt: string;
  displayText?: string;
  description?: string;
}

export interface CopilotPageInfoItem {
  term: string;
  description: string;
}

interface CopilotPagePromptsContextValue {
  prompts: CopilotPagePrompt[];
  infoItems: CopilotPageInfoItem[];
  infoTitle?: string;
  infoIntro?: string;
  setPagePrompts: (prompts: CopilotPagePrompt[]) => void;
  setPageInfoItems: (infoItems: CopilotPageInfoItem[]) => void;
  setPageInfoTitle: (title: string | undefined) => void;
  setPageInfoIntro: (intro: string | undefined) => void;
}

const CopilotPagePromptsContext = createContext<CopilotPagePromptsContextValue | undefined>(
  undefined
);

export const CopilotPagePromptsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [prompts, setPrompts] = useState<CopilotPagePrompt[]>([]);
  const [infoItems, setInfoItems] = useState<CopilotPageInfoItem[]>([]);
  const [infoTitle, setInfoTitle] = useState<string | undefined>(undefined);
  const [infoIntro, setInfoIntro] = useState<string | undefined>(undefined);

  useEffect(() => {
    setPrompts([]);
    setInfoItems([]);
    setInfoTitle(undefined);
    setInfoIntro(undefined);
  }, [location.pathname]);

  return (
    <CopilotPagePromptsContext.Provider
      value={{
        prompts,
        infoItems,
        infoTitle,
        infoIntro,
        setPagePrompts: setPrompts,
        setPageInfoItems: setInfoItems,
        setPageInfoTitle: setInfoTitle,
        setPageInfoIntro: setInfoIntro,
      }}
    >
      {children}
    </CopilotPagePromptsContext.Provider>
  );
};

export const usePageCopilotPrompts = (): CopilotPagePromptsContextValue => {
  const context = useContext(CopilotPagePromptsContext);
  if (!context) {
    throw new Error('usePageCopilotPrompts must be used within a CopilotPagePromptsProvider');
  }

  return context;
};
